/**
 * LaunchCountdownBanner — slim, MOBILE-ONLY top strip showing the estimated
 * public-launch date and a days-remaining countdown. Tapping it navigates to
 * the public changelog so visitors can follow along.
 *
 * - Mobile only (`md:hidden`); hidden on tablet/desktop where the full
 *   header already surfaces launch messaging.
 * - Days only (no hours/minutes) — kept intentionally simple + professional.
 * - Mounted globally in App.js above the routed content so it appears on
 *   every page.
 * - After the launch date passes, it switches to a "now live" message; once
 *   well past launch it renders nothing.
 */
import { Link } from 'react-router-dom';
import { Rocket, ChevronRight } from 'lucide-react';
import { useLanguage } from '@/lib/i18n';

// Estimated public launch — December 31, 2026 (JST end-of-year target).
const LAUNCH_DATE = new Date('2026-12-31T23:59:59+09:00');
const MS_PER_DAY = 1000 * 60 * 60 * 24;

function daysUntilLaunch() {
  const now = new Date();
  // floor → matches the desktop top-strip countdown (full days remaining).
  return Math.floor((LAUNCH_DATE.getTime() - now.getTime()) / MS_PER_DAY);
}

export default function LaunchCountdownBanner() {
  const { lang } = useLanguage();
  const ja = lang === 'ja';
  const days = daysUntilLaunch();

  // Hide entirely once we're more than a few days past launch.
  if (days < -3) return null;

  const launched = days <= 0;

  const label = launched
    ? (ja ? 'サイト公開中' : 'Now live')
    : (ja ? 'サイト公開予定 12月31日' : 'Launching Dec 31');

  const countdown = launched
    ? (ja ? '公開しました' : 'Live')
    : (ja ? `あと${days}日` : `${days} days`);

  return (
    <Link
      to="/changelog"
      data-testid="mobile-launch-countdown"
      aria-label={ja ? '公開までのカウントダウン。変更履歴を見る。' : 'Launch countdown. View the changelog.'}
      className="md:hidden block w-full bg-slate-900 text-white active:bg-slate-800 transition-colors"
    >
      <div className="px-4 py-2 flex items-center gap-2">
        <Rocket className="w-3.5 h-3.5 text-[#c77dff] flex-shrink-0" strokeWidth={2.4} />
        <span className="text-[12px] font-semibold leading-none truncate">{label}</span>
        <span className="ml-auto flex items-center gap-1 flex-shrink-0">
          <span
            data-testid="mobile-launch-countdown-days"
            className="inline-flex items-center rounded-full bg-[#9200C7] px-2 py-0.5 text-[11px] font-bold leading-none"
          >
            {countdown}
          </span>
          <ChevronRight className="w-3.5 h-3.5 opacity-70" strokeWidth={2.4} />
        </span>
      </div>
    </Link>
  );
}
